// Thrown from plain functions, not components — hence the i18next singleton
// rather than the useTranslation hook.
import { i18n } from "../../i18n";

const GRAPH = "https://graph.microsoft.com/v1.0";

export interface Contact {
  id: string;
  name: string;
  phones: string[];
}

export class GraphError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "GraphError";
  }
}

interface RawContact {
  id: string;
  displayName?: string;
  mobilePhone?: string | null;
  businessPhones?: string[];
  homePhones?: string[];
}

export async function getContacts(token: string, search?: string): Promise<Contact[]> {
  const params = new URLSearchParams({
    $select: "displayName,mobilePhone,businessPhones,homePhones",
    $top: "100",
    $orderby: "displayName",
  });
  const term = search?.trim();
  if (term) {
    params.set("$filter", `startswith(displayName,'${term.replace(/'/g, "''")}')`);
    params.delete("$orderby");
  }

  let response: Response;
  try {
    response = await fetch(`${GRAPH}/me/contacts?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    throw new GraphError(0, i18n.t("errors.graphNetwork", { detail: (err as Error).message }));
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    const message =
      response.status === 403
        ? i18n.t("errors.graphContactsDenied")
        : i18n.t("errors.graphApi", { status: response.status, detail }).trim();
    throw new GraphError(response.status, message);
  }

  const data = (await response.json()) as { value?: RawContact[] };
  return (data.value ?? [])
    .map((c) => ({
      id: c.id,
      name: c.displayName || "(no name)",
      phones: [c.mobilePhone ?? "", ...(c.businessPhones ?? []), ...(c.homePhones ?? [])].filter(
        Boolean
      ),
    }))
    .filter((c) => c.phones.length > 0);
}
